import React, { useMemo } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { AccessTimeOutlined, Add, BookmarkAddOutlined, Check, EditOutlined, EventBusy, PriorityHigh, RemoveCircleOutline, SwapHoriz, Sync, TaskAlt } from "@mui/icons-material";
import { colors } from "../../theme/designSystem";
import type { ProposalCardData } from "../../services/assistantsService";

// Cartão de proposta (item 228, F2d — D-OP-18, RN-566): uma alteração por cartão, embaixo da fala do agente de
// alteração. O selo diz o estado; os botões só aparecem quando há algo a decidir. Molde no canvas
// "Assistentes · Agenda" (aprovado pelo Hilton em 28/10).

interface Preview {
  titulo?: string;
  paciente?: string;
  profissional?: string;
  /** Tipo do profissional (ex.: Fonoaudiólogo), ao lado do nome */
  tipoProfissional?: string | null;
  /** Observações da sessão nova */
  observacoes?: string | null;
  de?: string;
  para?: string;
  sala?: string | null;
  quando?: string;
  motivo?: string;
  servico?: string;
  /** Inclusão/substituição: equipe da sessão nova */
  equipe?: string | null;
  /** Valor como a gravação vai calcular (unitário, desconto, total), já formatado pelo servidor */
  valor?: string | null;
  agendaData?: string;
  /** Id do paciente: a Agenda abre com ele já selecionado (o nome do cartão vem protegido) */
  pacienteId?: string | null;
  /** Substituição: o agendamento que sai (ex.: uma reserva). */
  sai?: string;
  /** Edição: o que muda além de data e horário (profissional, serviço, sala, observações). */
  mudancas?: string[];
  /** Série: "só esta sessão da série" ou "esta e as seguintes da série". */
  alcance?: string | null;
  /** Falso quando o assistente não consegue desfazer (série, profissional, serviço, inativação). */
  desfazer?: boolean;
  /** Livro-caixa e regra (RN-575): linhas prontas do servidor; `de` = valor de antes, riscado. */
  itens?: { rotulo: string; valor: string; de?: string | null }[];
}

interface Props {
  card: ProposalCardData;
  busy: boolean;
  isMobile: boolean;
  /** Erro da última ação neste cartão (rede, permissão); o estado vem do servidor. */
  error?: string | null;
  onConfirm: () => void;
  onDiscard: () => void;
  onUndo: () => void;
  onOpenAgenda?: (date: string | undefined, patientId: string | undefined) => void;
  /** Livro-caixa: abre a tela do livro (o cartão de regra não tem tela). */
  onOpenCashBook?: () => void;
}

const DONE_TITLE: Record<ProposalCardData["action"], string> = {
  schedule_insert: "Sessão incluída",
  schedule_reschedule: "Sessão alterada",
  schedule_inactivate: "Sessão inativada",
  schedule_replace: "Agendamento substituído",
  cashbook_insert: "Lançamento gravado",
  cashbook_settle_provision: "Provisão quitada",
  cashbook_settle: "Baixa registrada",
  cashbook_edit: "Lançamento alterado",
  cashbook_inactivate: "Lançamento excluído",
  memory_save: "Regra gravada",
};

const NOT_DONE_TITLE: Record<ProposalCardData["action"], string> = {
  schedule_insert: "Inclusão não gravada",
  schedule_reschedule: "Alteração não gravada",
  schedule_inactivate: "Inativação não gravada",
  schedule_replace: "Substituição não gravada",
  cashbook_insert: "Lançamento não gravado",
  cashbook_settle_provision: "Quitação não gravada",
  cashbook_settle: "Baixa não gravada",
  cashbook_edit: "Alteração não gravada",
  cashbook_inactivate: "Exclusão não gravada",
  memory_save: "Regra não gravada",
};

const UNDONE_LINE: Partial<Record<ProposalCardData["action"], string>> = {
  cashbook_insert: "Desfeito: o lançamento saiu do livro.",
  cashbook_settle_provision: "Desfeito: a quitação saiu do livro.",
  cashbook_settle: "Desfeito: o lançamento voltou a previsto.",
  cashbook_edit: "Desfeito: o lançamento voltou como estava.",
  memory_save: "Desfeito: a regra deixou de valer.",
};

const isCashBook = (action: ProposalCardData["action"]) => action.startsWith("cashbook_") || action === "memory_save";

const BADGE: Record<ProposalCardData["status"], { label: string; fg: string; bg: string; border: string }> = {
  pending: { label: "Aguardando você", fg: colors.warningSoftText, bg: colors.warningSoft, border: colors.border },
  confirmed: { label: "Gravado", fg: "#1e6b31", bg: colors.successSoft, border: "#b7dfc2" },
  rejected: { label: "Recusado", fg: "#a71d2a", bg: colors.errorSoft, border: "#f1b0b7" },
  expired: { label: "Expirou", fg: colors.textPrimary, bg: colors.backgroundAlt, border: colors.borderLight },
  discarded: { label: "Descartado", fg: colors.textPrimary, bg: colors.backgroundAlt, border: colors.borderLight },
  undone: { label: "Desfeito", fg: colors.textPrimary, bg: colors.backgroundAlt, border: colors.borderLight },
};

const ACTION_TONE: Record<ProposalCardData["action"], { bg: string; fg: string; icon: React.ReactNode }> = {
  schedule_insert: { bg: colors.successSoft, fg: "#1e6b31", icon: <Add /> },
  schedule_inactivate: { bg: colors.errorSoft, fg: "#a71d2a", icon: <EventBusy /> },
  schedule_reschedule: { bg: colors.primaryLight, fg: colors.primaryText, icon: <Sync /> },
  schedule_replace: { bg: colors.primaryLight, fg: colors.primaryText, icon: <SwapHoriz /> },
  cashbook_insert: { bg: colors.successSoft, fg: "#1e6b31", icon: <Add /> },
  cashbook_settle_provision: { bg: colors.successSoft, fg: "#1e6b31", icon: <TaskAlt /> },
  cashbook_settle: { bg: colors.successSoft, fg: "#1e6b31", icon: <TaskAlt /> },
  cashbook_edit: { bg: colors.primaryLight, fg: colors.primaryText, icon: <EditOutlined /> },
  cashbook_inactivate: { bg: colors.errorSoft, fg: "#a71d2a", icon: <RemoveCircleOutline /> },
  memory_save: { bg: colors.warningSoft, fg: colors.warningSoftText, icon: <BookmarkAddOutlined /> },
};

const parse = (json: string): Preview => {
  try {
    return JSON.parse(json) as Preview;
  } catch {
    return {};
  }
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <Typography component="span" sx={{ fontSize: "0.875rem", color: colors.textSecondary }}>{label}</Typography>
    <Typography component="span" sx={{ fontSize: "0.875rem", color: colors.textStrong, minWidth: 0, overflowWrap: "anywhere" }}>{children}</Typography>
  </>
);

const ProposalCard: React.FC<Props> = ({ card, busy, isMobile, error, onConfirm, onDiscard, onUndo, onOpenAgenda, onOpenCashBook }) => {
  const p = useMemo(() => parse(card.previewJson), [card.previewJson]);
  const badge = BADGE[card.status];
  const pending = card.status === "pending";
  const confirmed = card.status === "confirmed";
  const closed = card.status === "discarded" || card.status === "undone";
  const failed = card.status === "rejected" || card.status === "expired";
  const professional = p.profissional && (p.tipoProfissional ? `${p.profissional} (${p.tipoProfissional})` : p.profissional);
  const who = [p.paciente, professional && `com ${professional}`].filter(Boolean).join(" · ");
  const cash = isCashBook(card.action);
  const defaultTitle = cash ? "Alteração no Livro-caixa" : "Alteração na agenda";

  const title = confirmed ? DONE_TITLE[card.action] : failed ? NOT_DONE_TITLE[card.action] : p.titulo ?? defaultTitle;
  const tone = confirmed
    ? { bg: colors.successSoft, fg: "#1e6b31", icon: <Check /> }
    : failed
      ? card.status === "expired"
        ? { bg: colors.backgroundAlt, fg: colors.textPrimary, icon: <AccessTimeOutlined /> }
        : { bg: colors.errorSoft, fg: "#a71d2a", icon: <PriorityHigh /> }
      : ACTION_TONE[card.action];
  // Cartões antigos não trazem o campo: inativação nunca desfaz, o resto seguia a regra de antes
  const undoable = p.desfazer ?? (card.action !== "schedule_inactivate" && card.action !== "cashbook_inactivate");

  // Descartado e desfeito viram uma linha só: já não pedem atenção
  if (closed) {
    let line = "Nada foi gravado.";
    if (card.status === "undone" && cash) line = UNDONE_LINE[card.action] ?? "Desfeito.";
    else if (card.status === "undone" && card.action === "schedule_reschedule") line = `Remarcação desfeita: voltou para ${p.de ?? "o horário anterior"}.`;
    else if (card.status === "undone") line = "Desfeito: a sessão incluída foi inativada.";
    return (
      <Box sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: "8px", bgcolor: colors.white, px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.25, maxWidth: 520 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: colors.textSecondary, textDecoration: card.status === "discarded" ? "line-through" : "none" }}>
            {p.titulo ?? defaultTitle}{who ? ` · ${p.paciente}` : ""}
          </Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: colors.textSecondary }}>{line}</Typography>
        </Box>
        <Badge {...badge} />
      </Box>
    );
  }

  return (
    <Box component="section" aria-label={`${title}${p.paciente ? `, ${p.paciente}` : ""}`}
      sx={{ border: `1px solid ${badge.border}`, borderRadius: "8px", bgcolor: colors.white, overflow: "hidden", maxWidth: 520,
        boxShadow: pending ? "0 2px 10px rgba(33, 37, 41, 0.08)" : "none" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.5, borderBottom: `1px solid ${colors.backgroundSubtle}` }}>
        <Box aria-hidden sx={{ width: 32, height: 32, borderRadius: "8px", flexShrink: 0, bgcolor: tone.bg, color: tone.fg, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 18 } }}>
          {tone.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h3" sx={{ fontSize: "0.9375rem", fontWeight: 600, color: colors.textStrong }}>{title}</Typography>
          {who && <Typography sx={{ fontSize: "0.8125rem", color: colors.textSecondary, overflowWrap: "anywhere" }}>{who}</Typography>}
        </Box>
        {!isMobile && <Badge {...badge} />}
      </Box>

      {failed ? (
        <Typography sx={{ px: 2, py: 1.75, fontSize: "0.875rem", color: colors.textPrimary }}>
          {card.resultMessage ?? (card.status === "expired" ? `${cash ? "O lançamento" : "A sessão"} mudou depois da proposta. Peça uma proposta nova.` : "O sistema não aceitou a alteração.")}
        </Typography>
      ) : (
        <Box sx={{ px: 2, py: 1.75, display: "grid", gridTemplateColumns: cash ? "minmax(72px, 136px) minmax(0, 1fr)" : "64px minmax(0, 1fr)", rowGap: 0.75, columnGap: 1 }}>
          {isMobile && <><Typography component="span" sx={{ fontSize: "0.875rem", color: colors.textSecondary }}>Estado</Typography><Box><Badge {...badge} /></Box></>}
          {card.action === "schedule_reschedule" && (
            <>
              {p.de && <Row label="De"><Box component="span" sx={{ color: colors.textSecondary, textDecoration: "line-through" }}>{p.de}</Box></Row>}
              {p.para && <Row label="Para"><strong>{p.para}</strong></Row>}
              {p.quando && <Row label="Quando"><strong>{p.quando}</strong></Row>}
              {p.sala && <Row label="Sala">{p.sala}</Row>}
              {p.mudancas && p.mudancas.length > 0 && <Row label="Muda">{p.mudancas.join(" · ")}</Row>}
              {p.valor && <Row label="Valor">{p.valor}</Row>}
            </>
          )}
          {card.action === "schedule_inactivate" && (
            <>
              <Row label="Quando"><strong>{p.quando}</strong></Row>
              <Row label="Motivo">{p.motivo}</Row>
            </>
          )}
          {card.action === "schedule_insert" && (
            <>
              {p.servico && <Row label="Serviço">{p.servico}</Row>}
              <Row label="Quando"><strong>{p.quando}</strong></Row>
              {p.equipe && <Row label="Equipe">{p.equipe}</Row>}
              {p.equipe && <Row label="Sala">{p.sala ?? "Sem sala definida"}</Row>}
              {p.valor && <Row label="Valor">{p.valor}</Row>}
            </>
          )}
          {card.action === "schedule_replace" && (
            <>
              <Row label="Sai"><Box component="span" sx={{ color: colors.textSecondary, textDecoration: "line-through" }}>{p.sai}</Box></Row>
              <Row label="Entra"><strong>{p.quando}</strong>{p.servico ? ` · ${p.servico}` : ""}</Row>
              {p.equipe && <Row label="Equipe">{p.equipe}</Row>}
              {p.equipe && <Row label="Sala">{p.sala ?? "Sem sala definida"}</Row>}
              {p.valor && <Row label="Valor">{p.valor}</Row>}
            </>
          )}
          {(p.itens ?? []).map((it, i) => (
            <Row key={i} label={it.rotulo}>
              {it.de && <Box component="span" sx={{ color: colors.textSecondary, textDecoration: "line-through", mr: 1 }}>{it.de}</Box>}
              {it.de ? <strong>{it.valor}</strong> : it.valor}
            </Row>
          ))}
          {p.observacoes && <Row label="Observações">{p.observacoes}</Row>}
          {p.alcance && <Row label="Série">{p.alcance}</Row>}
        </Box>
      )}

      {error && (
        <Typography role="alert" sx={{ px: 2, pb: 1.25, fontSize: "0.8125rem", color: colors.errorHover }}>{error}</Typography>
      )}

      {pending && (
        <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 1, px: 2, py: 1.5, bgcolor: colors.background, borderTop: `1px solid ${colors.backgroundSubtle}` }}>
          {busy ? (
            <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1.25, minHeight: 40, fontSize: "0.875rem", color: colors.textPrimary }}>
              <CircularProgress size={16} aria-hidden /> {cash ? "Gravando no livro…" : "Gravando na agenda…"}
            </Box>
          ) : (
            <>
              <Button variant="contained" onClick={onConfirm} sx={{ minHeight: isMobile ? 44 : 40, px: 2.5, fontWeight: 600, textTransform: "none", boxShadow: "none" }}>Confirmar</Button>
              <Button variant="outlined" onClick={onDiscard} sx={{ minHeight: isMobile ? 44 : 40, px: 2, textTransform: "none", color: colors.textPrimary, borderColor: colors.border }}>Descartar</Button>
            </>
          )}
        </Box>
      )}

      {confirmed && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, px: 2, py: 1.5, bgcolor: colors.background }}>
          {(cash ? card.action !== "memory_save" && onOpenCashBook : onOpenAgenda) && (
            <Button variant="outlined" onClick={() => (cash ? onOpenCashBook?.() : onOpenAgenda?.(p.agendaData, p.pacienteId ?? undefined))}
              sx={{ minHeight: isMobile ? 44 : 40, px: 2, fontWeight: 600, textTransform: "none", color: colors.primaryText, borderColor: colors.border, bgcolor: colors.white }}>
              {cash ? "Ver no Livro-caixa" : "Ver na agenda"}
            </Button>
          )}
          {undoable ? (
            <Button onClick={onUndo} disabled={busy}
              sx={{ minHeight: isMobile ? 44 : 40, px: 1.5, textTransform: "none", color: colors.textPrimary, textDecoration: "underline", textUnderlineOffset: "3px" }}>
              {busy ? "Desfazendo…" : "Desfazer"}
            </Button>
          ) : (
            <Typography sx={{ flexBasis: "100%", fontSize: "0.75rem", color: colors.textSecondary }}>
              {notUndoableText(card.action)}
            </Typography>
          )}
          {card.action === "schedule_replace" && undoable && (
            <Typography sx={{ flexBasis: "100%", fontSize: "0.75rem", color: colors.textSecondary }}>
              Desfazer inativa a sessão incluída; o agendamento que saiu não volta.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const notUndoableText = (action: ProposalCardData["action"]) => {
  if (action === "schedule_inactivate") return "Inativação não se desfaz pelo assistente: para voltar, crie a sessão de novo pela Agenda.";
  if (action === "cashbook_inactivate") return "Exclusão não se desfaz pelo assistente: para voltar, lance de novo pelo Livro-caixa.";
  return isCashBook(action)
    ? "Esta alteração não se desfaz pelo assistente: para voltar, ajuste pelo Livro-caixa."
    : "Esta alteração não se desfaz pelo assistente: para voltar, ajuste pela Agenda.";
};

const Badge: React.FC<{ label: string; fg: string; bg: string }> = ({ label, fg, bg }) => (
  <Box component="span" sx={{ fontSize: "0.75rem", fontWeight: 600, color: fg, bgcolor: bg, px: 1.25, py: 0.375, borderRadius: "10px", whiteSpace: "nowrap", flexShrink: 0 }}>
    {label}
  </Box>
);

export default ProposalCard;
